import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, ThemeProvider } from "@mui/material";
import { theme } from "./index.styles";
import "./tabs.css";

function TabPanel(props) {
  const { children, value, name, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${name}-tabpanel`}
      aria-labelledby={`${name}-tabpanel`}
      {...other}
      style={{ cursor: "pointer" }}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(name) {
  return {
    id: `tab-btn-${name}`,
    "aria-controls": `${name}-tabpanel`,
  };
}

// Pill styling matches the native app's "pills" tab variant (TabView,
// src/components/organisms/TabView): rounded, bordered chips instead of an
// underline indicator. Applied via `sx` so it only affects callers that opt
// in with variant="pills" — every other TabView usage is unaffected.
const PILLS_TABS_SX = {
  borderBottom: "none",
  minHeight: 0,
  padding: 0,
  "& .MuiTabs-flexContainer": { gap: "8px" },
};
const pillTabSx = (selected) => ({
  color: selected ? "#fff !important" : "#111827",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.82rem",
  minHeight: 36,
  padding: "8px 16px",
  borderRadius: "20px",
  border: "1px solid #E5E7EB !important",
  background: (selected ? "#20A64A" : "#fff") + " !important",
  "&:hover": {
    backgroundColor: selected ? "#20A64A" : "rgba(17,24,39,0.04)",
    color: selected ? "#fff" : "#111827",
  },
  "&.Mui-selected": {
    background: "#20A64A !important",
    border: "1px solid transparent !important",
    borderTop: "1px solid transparent !important",
    borderLeft: "1px solid transparent !important",
    borderRight: "1px solid transparent !important",
    borderBottom: "1px solid transparent !important",
  },
});

const TabView = ({
  tabs,
  renderActiveTabContent,
  onTabChane,
  subUuid,
  setTabindexs,
  tabIndex = 0,
  variant = "default",
}) => {
  const isPillsVariant = variant === "pills";
  const [value, setValue] = useState(tabIndex);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    setValue(tabIndex);
  }, [tabIndex]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (_e, newValue) => {
    setTabindexs(newValue);
    setValue(newValue);
    onTabChane(tabs[newValue].uuid, newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="basic tabs example"
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
        sx={isPillsVariant ? PILLS_TABS_SX : undefined}
        TabIndicatorProps={isPillsVariant ? { style: { display: "none" } } : undefined}
        // centered={!isMobile}
      >
        {tabs?.map((item, idx) => (
          <Tab
            key={item?.uuid || idx}
            sx={isPillsVariant ? pillTabSx(value === idx) : undefined}
            label={
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {item?.image && (
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span>{item?.name}</span>
                {item.mails_count > 0 && (
                  <span
                    style={{
                      background:
                        "linear-gradient(135deg, #ffe066, #ffd54f, #ffb300)",
                      color: "#8a5700",
                      borderRadius: "10px",
                      padding: "1px 6px",
                      fontSize: "0.68rem",
                      fontWeight: "700",
                      minWidth: "18px",
                      textAlign: "center",
                      lineHeight: "1.4",
                    }}
                  >
                    {item.mails_count}
                  </span>
                )}
              </div>
            }
            {...a11yProps(item?.name)}
          />
        ))}
      </Tabs>

      {tabs?.map((item, idx) => (
        <TabPanel
          key={item?.uuid || idx}
          value={value}
          name={item?.name}
          index={idx}
        >
          {renderActiveTabContent(tabs[value])}
        </TabPanel>
      ))}
    </ThemeProvider>
  );
};

export default TabView;
